/**
 * Sound Manager for LoLo Solitaire
 * Handles playback of game sound effects with optional pitch-shifting
 */

interface SoundResource {
    mp3?: string;
    webm?: string;
}

interface SoundLibrary {
    [key: string]: SoundResource;
}

let audioContext: AudioContext | null = null;
let masterGain: GainNode | null = null;
let soundEnabled = true;

/**
 * Initialize the audio context if not already done
 */
export function ensureAudioContext(): AudioContext {
    if (!audioContext) {
        audioContext = new (window.AudioContext ||
            (window as any).webkitAudioContext)();
        masterGain = audioContext.createGain();
        masterGain.connect(audioContext.destination);
    }

    if (audioContext.state === "suspended") {
        audioContext.resume().catch(() => { });
    }

    return audioContext;
}

/**
 * Update master gain based on sound enabled state
 */
export function updateMasterGain(enabled: boolean) {
    soundEnabled = enabled;
    if (audioContext && masterGain) {
        const now = audioContext.currentTime;
        masterGain.gain.cancelScheduledValues(now);
        masterGain.gain.setValueAtTime(masterGain.gain.value, now);

        if (enabled) {
            masterGain.gain.linearRampToValueAtTime(1, now + 0.1);
        } else {
            masterGain.gain.linearRampToValueAtTime(0, now + 0.3);
        }
    }
}

/**
 * Play a sound effect with optional pitch-shifting
 * @param soundUrl URL of the sound file
 * @param pitchShift Pitch multiplier (e.g., 1.0 = normal, 1.5 = up half octave, 0.5 = down)
 * @param volume Volume multiplier (0-1)
 * @param fadeInDuration Duration of fade-in in seconds
 */
export async function playSound(
    soundUrl: string,
    pitchShift: number = 1.0,
    volume: number = 1.0,
    fadeInDuration: number = 0.05,
): Promise<void> {
    if (!soundEnabled) return;

    if (import.meta.env.DEV) {
        console.log(`[Sound] Playing: ${soundUrl.split('/').pop()} (pitch: ${pitchShift.toFixed(2)}, volume: ${volume.toFixed(2)})`);
    }

    try {
        const ctx = ensureAudioContext();

        const response = await fetch(soundUrl);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await ctx.decodeAudioData(arrayBuffer);

        const source = ctx.createBufferSource();
        source.buffer = audioBuffer;

        // Apply pitch shift if needed
        if (pitchShift !== 1.0) {
            source.playbackRate.value = pitchShift;
        }

        // Create gain node for this sound
        const gainNode = ctx.createGain();
        source.connect(gainNode);

        if (masterGain) {
            gainNode.connect(masterGain);
        } else {
            gainNode.connect(ctx.destination);
        }

        // Set up fade in
        const now = ctx.currentTime;
        gainNode.gain.setValueAtTime(0, now);
        gainNode.gain.linearRampToValueAtTime(volume, now + fadeInDuration);

        // Fade out at the end
        const duration = audioBuffer.duration / pitchShift;
        const fadeOutStart = Math.max(now + fadeInDuration, now + duration - 0.2);
        gainNode.gain.setValueAtTime(volume, fadeOutStart);
        gainNode.gain.linearRampToValueAtTime(0, now + duration);

        source.start(now);
    } catch (e) {
        console.error("Failed to play sound:", e);
    }
}

/**
 * Play a random sound from a library
 * @param soundLibrary Object with sound names as keys and SoundResource objects as values
 * @param pitchShift Pitch multiplier
 * @param volume Volume multiplier
 */
export async function playRandomSound(
    soundLibrary: SoundLibrary,
    pitchShift: number = 1.0,
    volume: number = 1.0,
): Promise<void> {
    const keys = Object.keys(soundLibrary);
    if (keys.length === 0) return;

    const randomKey = keys[Math.floor(Math.random() * keys.length)];
    const soundResource = soundLibrary[randomKey];

    const soundUrl = selectBestAudioFormat(soundResource);
    if (soundUrl) {
        await playSound(soundUrl, pitchShift, volume);
    }
}

/**
 * Select the best audio format based on browser support
 */
function selectBestAudioFormat(resource: SoundResource): string | null {
    if (!resource.mp3 && !resource.webm) return null;

    // Check for Opus support
    const audioTest = new Audio();
    const supportsOpus =
        audioTest.canPlayType('audio/webm; codecs="opus"') === "probably" ||
        audioTest.canPlayType('audio/webm; codecs="opus"') === "maybe";

    if (supportsOpus && resource.webm) {
        return resource.webm;
    }

    return resource.mp3 || null;
}

/**
 * Calculate pitch shift based on card count
 * Used for "picking up X cards" sounds
 * Formula: 2^(semitones/12) where semitones = cardCount - 1
 * Each card adds one semitone (one note)
 */
export function getPitchForCardCount(cardCount: number): number {
    if (cardCount <= 0) return 1.0;
    const semitones = Math.max(-12, Math.min(12, cardCount - 1));
    return Math.pow(2, semitones / 12);
}

/**
 * Calculate pitch shift based on card rank
 * Ace=1 (lowest), King=13 (highest)
 * Formula: 2^(semitones/12) where semitones = (rank - 1)
 * Each rank adds one semitone (one note)
 */
export function getPitchForCardRank(rank: number): number {
    if (rank < 1 || rank > 13) return 1.0;
    const semitones = rank - 1;
    return Math.pow(2, semitones / 12);
}

/**
 * Calculate pitch shift based on foundation pile size
 * Formula: 2^(semitones/12) where semitones = (pileSize - 1)
 * Each card in foundation adds one semitone (one note)
 */
export function getPitchForFoundationSize(pileSize: number): number {
    if (pileSize <= 0) return 1.0;
    const semitones = Math.max(-12, Math.min(12, pileSize - 1));
    return Math.pow(2, semitones / 12);
}
